/**
 * Date display/parsing helpers for Live URL Tracking search criteria (MM/DD/YYYY).
 * Internal storage uses YYYY-MM-DD for API compatibility.
 */

export const DATE_DISPLAY_PLACEHOLDER = 'MM/DD/YYYY';

/** @param {string|null|undefined} isoDate YYYY-MM-DD */
export const isoDateToDisplay = (isoDate) => {
  if (!isoDate) return '';
  const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  return `${match[2]}/${match[3]}/${match[1]}`;
};

/** @param {string} display MM/DD/YYYY */
export const displayDateToIso = (display) => {
  const trimmed = (display || '').trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) {
    return null;
  }

  const iso = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const parsed = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;

  return iso;
};

const MAX_DATE_RANGE_DAYS = 365;

/** @returns {string} YYYY-MM-DD for today in local time */
export const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** @param {string|null} fromIso @param {string|null} toIso */
export const isDateRangeInvalid = (fromIso, toIso) => {
  if (!fromIso || !toIso) return false;
  return fromIso > toIso;
};

/** @param {string|null} fromIso @param {string|null} toIso */
export const isDateRangeExceedsOneYear = (fromIso, toIso) => {
  if (!fromIso || !toIso || isDateRangeInvalid(fromIso, toIso)) return false;

  const from = new Date(`${fromIso}T00:00:00.000Z`);
  const to = new Date(`${toIso}T00:00:00.000Z`);
  const diffDays = (to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000);

  return diffDays > MAX_DATE_RANGE_DAYS;
};

/** @param {string|null|undefined} isoDate YYYY-MM-DD */
export const isFutureDate = (isoDate) => {
  if (!isoDate) return false;
  return isoDate > getTodayIsoDate();
};

/** @param {string|null} fromIso @param {string|null} toIso @returns {string} */
export const getDateRangeError = (fromIso, toIso) => {
  if (fromIso && isFutureDate(fromIso)) {
    return 'From date cannot be in the future.';
  }
  if (toIso && isFutureDate(toIso)) {
    return 'To date cannot be in the future.';
  }
  if (!fromIso || !toIso) return '';
  if (isDateRangeInvalid(fromIso, toIso)) {
    return 'From date must be before to date';
  }
  if (isDateRangeExceedsOneYear(fromIso, toIso)) {
    return 'Date range cannot exceed one year.';
  }
  return '';
};
